import React, { createContext, useContext, useEffect, useState, ReactNode } from "react"

// allow an array of either environments or objects with more conditions
export type FeatureFlagCondition = FeatureEnableEnv | { envs: FeatureEnableEnv[]; emails?: string[] }

export type FeatureEnableEnv = "local" | "dev" | "staging" | "industry" | "prod" | "all"

export type FeatureFlags<T extends Record<string, FeatureFlagCondition[]>> = {
  [K in keyof T]: boolean
}

export interface FeatureFlagProps<T extends Record<string, FeatureFlagCondition[]>> {
  featureFlagConfig: T
  children: ReactNode
  userEmail?: string
}

const evaluateCondition = (condition: FeatureFlagCondition, currentEnv: FeatureEnableEnv, userEmail?: string, domain = ""): boolean => {
  const isStagingDomain = domain.includes("staging")
  const isIndustryDomain = domain.includes("industry")

  const matchesEnv = (env: FeatureEnableEnv): boolean => {
    return (
      env === "all" ||
      (env === "local" && currentEnv === "local") ||
      (env === "dev" && currentEnv === "dev") ||
      (env === "prod" && currentEnv === "prod") ||
      (env === "staging" && currentEnv === "staging" && isStagingDomain) ||
      (env === "industry" && currentEnv === "staging" && isIndustryDomain)
    )
  }

  if (typeof condition === "string") {
    return matchesEnv(condition)
  }

  const envMatch = condition.envs.some(matchesEnv)
  const emailMatch = condition.emails ? emailMatches(condition.emails, userEmail) : true

  return envMatch && emailMatch
}

const emailMatches = (emailList: string[] = [], userEmail?: string): boolean => {
  if (!userEmail) return false
  return emailList.some((entry) => {
    return entry.startsWith("@")
      ? userEmail.endsWith(entry) // wildcard domain match
      : userEmail === entry // exact match
  })
}

export const getFlags = <T extends Record<string, FeatureFlagCondition[]>>(featureFlagConfig: T, userEmail?: string): FeatureFlags<T> => {
  const res = {} as FeatureFlags<T>
  const currentEnv = process.env.FEATURE_ENV as FeatureEnableEnv
  const domain = typeof window !== "undefined" ? window.location.hostname : ""

  for (let flagName in featureFlagConfig) {
    const conditions = featureFlagConfig[flagName]
    res[flagName] = conditions.some((condition) => evaluateCondition(condition, currentEnv, userEmail, domain))
  }

  return res
}

const Ctx = createContext<FeatureFlags<any>>({})

export const FeatureFlagProvider = <T extends Record<string, FeatureFlagCondition[]>>({
  children,
  featureFlagConfig,
  userEmail,
}: FeatureFlagProps<T>) => {
  const [flags, setFlags] = useState<FeatureFlags<T>>(getFlags(featureFlagConfig, userEmail))

  useEffect(() => {
    setFlags(getFlags(featureFlagConfig, userEmail))
  }, [featureFlagConfig, userEmail])

  return <Ctx.Provider value={flags}>{children}</Ctx.Provider>
}

export function useFlags<T extends Record<string, FeatureFlagCondition[]>>(): FeatureFlags<T> {
  const context = useContext(Ctx)
  return context
}

export default useFlags

// Old Version
// import React, { createContext, useContext, useEffect, useState, ReactNode } from "react"

// // Define the possible environments for feature flags
// export type FeatureEnableEnv = "local" | "dev" | "staging" | "industry" | "prod" | "all"

// // Define the structure of feature flags dynamically based on the provided config
// export type FeatureFlags<T extends Record<string, FeatureEnableEnv[]>> = {
//   [K in keyof T]: boolean
// }

// // Define the props for the FeatureFlagProvider
// export interface FeatureFlagProps<T extends Record<string, FeatureEnableEnv[]>> {
//   featureFlagConfig: T // Expecting feature flags configuration
//   children: ReactNode // Define children as ReactNode
// }

// // Function to translate the feature flag config to flags
// export const getFlags = <T extends Record<string, FeatureEnableEnv[]>>(featureFlagConfig: T): FeatureFlags<T> => {
//   const res = {} as FeatureFlags<T>
//   const currentEnv = process.env.FEATURE_ENV as FeatureEnableEnv
//   const domain = typeof window !== "undefined" ? window.location.hostname : ""
//   const isIndustryDomain = domain.includes("industry")
//   const isStagingDomain = domain.includes("staging")

//   for (let flagName in featureFlagConfig) {
//     const flagEnableEnvs = featureFlagConfig[flagName]
//     const isEnabled =
//       flagEnableEnvs.includes("all") ||
//       (flagEnableEnvs.includes("local") && currentEnv === "local") ||
//       (flagEnableEnvs.includes("dev") && currentEnv === "dev") ||
//       (flagEnableEnvs.includes("prod") && currentEnv === "prod") ||
//       (flagEnableEnvs.includes("staging") && currentEnv === "staging" && isStagingDomain) ||
//       (flagEnableEnvs.includes("industry") && currentEnv === "staging" && isIndustryDomain)

//     res[flagName] = isEnabled
//   }
//   return res
// }

// // Create a context with a default empty object
// const Ctx = createContext<FeatureFlags<any>>({})

// // Define the FeatureFlagProvider component with inferred feature flag keys
// export const FeatureFlagProvider = <T extends Record<string, FeatureEnableEnv[]>>({ children, featureFlagConfig }: FeatureFlagProps<T>) => {
//   const [flags, setFlags] = useState<FeatureFlags<T>>(getFlags(featureFlagConfig))

//   useEffect(() => {
//     setFlags(getFlags(featureFlagConfig)) // Get the updated flags based on provided config
//   }, [featureFlagConfig]) // Update flags when featureFlagConfig changes

//   return <Ctx.Provider value={flags}>{children}</Ctx.Provider>
// }

// // Create a custom hook to use feature flags with inferred keys
// export function useFlags<T extends Record<string, FeatureEnableEnv[]>>(): FeatureFlags<T> {
//   const context = useContext(Ctx)
//   return context
// }

// export default useFlags
